import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Paper,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DropdownTextField from "../form-components/DropdownTextField";
import { SectionLabel } from "../portals/PortalShared";
import { v4 as uuid } from "uuid";
import LocalText from "../common/LocalText";
import { hasRequiredName } from "../../utils/validators";

const MEDIA_TYPES = [
  { value: 1, label: "Image", type: "image" },
  { value: 2, label: "Video", type: "video" },
  { value: 3, label: "Audio", type: "audio" },
];

const DEFAULT_LOCALE = "en_us";

const MediaModal = ({
  open, onClose, onSave,
  initialMediaTypeId,
  initialTitlePairs,
  initialDescriptionPairs,
  initialLinkPairs,
  isEditing = false,
  readOnly = false,
  title,
  subtitle,
  saveLabel,
}) => {
  const [mediaTypeId, setMediaTypeId] = useState(1);
  const [titlePairs, setTitlePairs] = useState([{ localeId: DEFAULT_LOCALE, value: "" }]);
  const [descriptionPairs, setDescriptionPairs] = useState([{ localeId: DEFAULT_LOCALE, value: "" }]);
  const [linkPairs, setLinkPairs] = useState([{ localeId: DEFAULT_LOCALE, mediaLink: "" }]);
  const [mediaUrlsValid, setMediaUrlsValid] = useState(true);

  useEffect(() => {
    if (open) {
      setMediaTypeId(initialMediaTypeId ?? 1);
      setTitlePairs(initialTitlePairs?.length ? initialTitlePairs : [{ localeId: DEFAULT_LOCALE, value: "" }]);
      setDescriptionPairs(initialDescriptionPairs?.length ? initialDescriptionPairs : [{ localeId: DEFAULT_LOCALE, value: "" }]);
      setLinkPairs(initialLinkPairs?.length ? initialLinkPairs : [{ localeId: DEFAULT_LOCALE, mediaLink: "" }]);
      setMediaUrlsValid(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const currentType = MEDIA_TYPES.find((m) => m.value === mediaTypeId)?.type ?? "image";
  const hasLink = linkPairs.some((p) => (p.mediaLink ?? '').trim());
  const isValid = hasLink && hasRequiredName(titlePairs) && hasRequiredName(descriptionPairs) && mediaUrlsValid;

  const handleMediaValidation = useCallback((allValid) => setMediaUrlsValid(allValid), []);

  const handleSave = () => {
    const payload = {
      mediaTypeId,
      textKey: uuid(),
      textLocalizations: titlePairs
        .filter((p) => p.value)
        .map((p) => ({ localeId: p.localeId, value: p.value })),
      descriptionKey: uuid(),
      descriptionLocalizations: descriptionPairs
        .filter((p) => p.value)
        .map((p) => ({ localeId: p.localeId, value: p.value })),
      linkLocalizations: linkPairs
        .filter((p) => (p.mediaLink ?? '').trim())
        .map((p) => ({ localeId: p.localeId, mediaLink: p.mediaLink })),
      modifiedBy: "system",
    };
    onSave(payload);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle sx={{ bgcolor: "primary.main", color: "white", pr: 6, py: 2.5 }}>
        <Typography variant="h6" fontWeight={700} component="div">
          <LocalText>{title ?? (readOnly ? "View Media" : isEditing ? "Edit Media" : "New Media")}</LocalText>
        </Typography>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", mt: 0.25, display: "block" }}>
          <LocalText>{subtitle ?? (readOnly ? "Current live content (read-only)" : isEditing ? "Update this media item" : "Add a media item to this booth")}</LocalText>
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 10, top: 12, color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>

        {/* Media Type */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
          <SectionLabel>Media Type</SectionLabel>
          <RadioGroup
            row
            value={mediaTypeId}
            onChange={(e) => !readOnly && setMediaTypeId(Number(e.target.value))}
            sx={{ mt: 0.5 }}
          >
            {MEDIA_TYPES.map((m) => (
              <FormControlLabel
                key={m.value}
                value={m.value}
                control={<Radio size="small" />}
                label={<LocalText>{m.label}</LocalText>}
                sx={{ mr: 2 }}
              />
            ))}
          </RadioGroup>
        </Paper>

        <Divider />

        {/* Title */}
        <Box>
          <SectionLabel required>Title</SectionLabel>
          <DropdownTextField
            label="Title"
            values={titlePairs}
            attribute="value"
            onChange={setTitlePairs}
            disabled={readOnly}
            required={!readOnly}
          />
        </Box>

        {/* Description */}
        <Box>
          <SectionLabel required>Description</SectionLabel>
          <DropdownTextField
            label="Description"
            values={descriptionPairs}
            attribute="value"
            onChange={setDescriptionPairs}
            disabled={readOnly}
            required={!readOnly}
          />
        </Box>

        <Divider />

        {/* Media URL */}
        <Box>
          <SectionLabel required>Media URL</SectionLabel>
          <DropdownTextField
            label="Media URL"
            values={linkPairs}
            attribute="mediaLink"
            onChange={setLinkPairs}
            previewable={!readOnly}
            mediaType={currentType}
            urlValidationType={readOnly ? null : currentType}
            onValidationChange={readOnly ? undefined : handleMediaValidation}
            disabled={readOnly}
            required={!readOnly}
          />
        </Box>

      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", p: 3, bgcolor: "grey.50" }}>
        {readOnly ? (
          <Button
            variant="outlined"
            onClick={onClose}
            size="large"
            sx={{ px: 8, py: 1.5, fontWeight: 700, borderRadius: 2, width: "100%" }}
          >
            <LocalText>Close</LocalText>
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isValid}
            size="large"
            sx={{ px: 8, py: 1.5, fontWeight: 700, borderRadius: 2, width: "100%" }}
          >
            <LocalText>{saveLabel ?? (isEditing ? "Update Media" : "Save Media")}</LocalText>
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MediaModal;
